import './RadioGroup.scss'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { clx } from '../clx'

export interface RadioOption<T extends string = string> {
  value: T
  label: ReactNode
  /** A second line under the label. */
  description?: ReactNode
  /** Decorative icon before the label. */
  icon?: ReactNode
  disabled?: boolean
}

export interface RadioGroupProps<T extends string = string> extends Omit<
  ComponentProps<'fieldset'>,
  'onChange' | 'children' | 'defaultValue'
> {
  /** The group's name (the legend). */
  label: ReactNode
  /** The radios' shared `name`. Default: a generated one. */
  name?: string
  options: readonly RadioOption<T>[]
  /** Controlled: `null`/`undefined` while nothing is chosen. */
  value: T | null | undefined
  onChange: (value: T) => void
  /**
   * `inline`: radios in a row that wraps (react-admin's RadioButtonGroupInput).
   * `list`: one selectable row per option, with an icon and a description
   * (MUI's List of ListItemButtons). Default `inline`.
   */
  variant?: 'inline' | 'list'
  hint?: ReactNode
  error?: ReactNode
  isRequired?: boolean
  isDisabled?: boolean
}

/**
 * react-admin's RadioButtonGroupInput: one choice from a few, all visible.
 * Native radios in a `fieldset`, so arrow keys, form submission and screen
 * readers behave as the browser defines; the legend names the group and the
 * hint and error describe it, as `Field` does for one control.
 */
export function RadioGroup<T extends string = string>({
  label,
  name,
  options,
  value,
  onChange,
  variant = 'inline',
  hint,
  error,
  isRequired = false,
  isDisabled = false,
  className,
  ...rest
}: RadioGroupProps<T>) {
  const id = `radio-group-${useId()}`
  const groupName = name ?? id
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <fieldset
      {...rest}
      disabled={isDisabled || undefined}
      {...(describedBy && { 'aria-describedby': describedBy })}
      className={clx(
        'radio-group',
        variant === 'list' && 'radio-group--as-list',
        error != null && 'radio-group--has-error',
        className
      )}
    >
      <legend className="radio-group__legend">
        {label}
        {isRequired && (
          <span className="radio-group__required" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </legend>
      <div className="radio-group__options">
        {options.map((option) => {
          const descriptionId = option.description ? `${id}-${option.value}-description` : undefined
          return (
            <label key={option.value} className="radio-group__option">
              <input
                type="radio"
                className="radio-group__input"
                name={groupName}
                value={option.value}
                checked={value === option.value}
                disabled={option.disabled}
                required={isRequired || undefined}
                {...(error != null && { 'aria-invalid': true })}
                {...(descriptionId && { 'aria-describedby': descriptionId })}
                onChange={() => {
                  onChange(option.value)
                }}
              />
              {option.icon != null && (
                <span className="radio-group__icon" aria-hidden="true">
                  {option.icon}
                </span>
              )}
              <span className="radio-group__text">
                <span className="radio-group__label">{option.label}</span>
                {option.description != null && (
                  <span id={descriptionId} className="radio-group__description">
                    {option.description}
                  </span>
                )}
              </span>
            </label>
          )
        })}
      </div>
      {error != null && (
        <p id={errorId} className="radio-group__error">
          {error}
        </p>
      )}
      {hint != null && (
        <p id={hintId} className="radio-group__hint">
          {hint}
        </p>
      )}
    </fieldset>
  )
}
